<template>
  <div class="boost-grid container-no-padding">
    <boost-filter
      :boost-initial="boostInitial"
      :boost-ready="boostReady"
      :choices="choices"
      :sort-by-options="sortByOptions"
      :tab="tab"
      :total="total"
      @choices-updated="choicesUpdated"
      @clear-choices="clearChoices"
      @facets-ready="updateChoicesOnLoad"
      @update-tab="handleUpdateTab"
    />

    <template v-if="boostReady">
      <div
        v-if="noCurrent"
        class="boost-filter__error-message"
        v-text="$string(`search.no.${tab}`)"
      />

      <div
        class="boost-grid__grid grid"
        :class="[
          `boost-grid__grid--${tab}`,
          { 'container': tab !== 'products' },
        ]"
      >
        <!-- Products -->
        <template v-if="tab === 'products'">
          <template
            v-for="(product, index) of products"
            :key="`product-card-${liquid.sectionId}-${product.id}-${index}`"
          >
            <div
              v-if="inGridContentBannerAvailable && (index === inGridContentBannerPosition - 1)"
              :class="inGridContentBannerWidth"
            >
              <in-grid-content-banner
                :banner-image="inGridContentBanner.bannerImage"
                :banner-link="inGridContentBanner.bannerLink"
                :banner-text="inGridContentBanner.bannerText"
                :banner-video="inGridContentBanner.bannerVideo"
                :banner-width="inGridContentBanner.bannerWidth"
              />
            </div>

            <div class="col xs3 l3">
              <product-card
                v-if="product.id"
                block-class="boost-grid"
                delay-click
                :index="index"
                :product="product"
                :lazyload="index < 4 ? false : 'lazy'"
                small
                @view-product="handleViewProduct"
              />
            </div>
          </template>
        </template>

        <!-- Collections -->
        <template v-else-if="tab === 'collections'">
          <div
            v-for="(collection, index) of collections"
            :key="`collection-${index}`"
            class="col xs6 l4"
          >
            <a
              class="
                boost-grid__collection
                text-heading-2xs
                text-heading-xs-desktop
                text-center
              "
              :href="collection.url"
              v-text="collection.title"
            />
          </div>
        </template>

        <!-- Pages -->
        <template v-else-if="tab === 'pages'">
          <div
            v-for="(pageObject, index) of pages"
            :key="`page-${index}`"
            class="col xs6 l3-10"
          >
            <div class="boost-grid__page">
              <p
                class="
                  boost-grid__page-title
                  text-heading-xs
                  text-heading-s-desktop
                "
                v-text="pageObject.title"
              />

              <p
                v-if="pageObject.content"
                class="boost-grid__page-text text-body-xs text-body-s-desktop"
                v-text="pageObject.content"
              />

              <btn
                class="boost-grid__page-button"
                :label="$string('search.read_more')"
                modifiers="text"
                :show-icon="false"
                :url="pageObject.url"
              />
            </div>
          </div>
        </template>
      </div>
    </template>

    <!-- Loading -->
    <div
      v-else
      class="boost-grid__grid grid"
      :class="[
        $class('loading'),
        `boost-grid__grid--${tab}`,
        { 'container': tab !== 'products' },
      ]"
    >
      <template v-if="tab === 'products'">
        <div
          v-for="(index) of $variable('settings.pagination.products')"
          :key="`product-card-${index}`"
          class="col xs3 l3"
        >
          <product-card
            block-class="boost-grid"
            :index="index"
            :product="false"
            small
          />
        </div>
      </template>

      <template v-else-if="tab === 'collections'">
        <div
          v-for="(index) of $variable('settings.pagination.collections')"
          :key="`collection-${index}`"
          class="col xs6 l4"
        >
          <div
            class="
              boost-grid__collection
              text-heading-2xs
              text-heading-xs-desktop
              text-center
            "
            v-text="$string('accessibility.loading')"
          />
        </div>
      </template>

      <template v-else-if="tab === 'pages'">
        <div
          v-for="(index) of $variable('settings.pagination.pages')"
          :key="`page-${index}`"
          class="col xs6 l3-10"
        >
          <div class="boost-grid__page">
            <p
              class="
              boost-grid__page-title
              text-heading-xs
              text-heading-s-desktop
            "
              v-text="$string('accessibility.loading')"
            />

            <p
              class="boost-grid__page-text text-body-xs text-body-s-desktop"
              v-text="$string('accessibility.loading')"
            />

            <btn
              class="boost-grid__page-button"
              :label="$string('accessibility.loading')"
              modifiers="text"
              type="button"
            />
          </div>
        </div>
      </template>
    </div>

    <boost-pagination
      class="boost-grid__pagination"
      :boost-ready="boostReady"
      :collections="collections"
      :default-link="linkWithoutPage"
      :limit="limit"
      :no-current="noCurrent"
      :page="page"
      :pages="pages"
      :products="products"
      :state="state"
      :tab="tab"
      :total-current="totalCurrent"
      @choices-updated="choicesUpdated"
    />

    <!-- API -->
    <boost-api-call
      build-filter-tree
      :choices="choices"
      :collection-id="liquid.id"
      :endpoint="endpoint"
      :limit="limit"
      :page="page"
      :query="query"
      :shop="$variable('store.localisedPermanentDomain')"
      :storage-key="storageKey"
      @boost-prices-updated="boostPricesUpdated"
      @boost-updated="boostUpdated"
    />

    <!-- Boost drawer -->
    <overlay
      key="boostDrawer"
      block-class="boost-drawer-overlay"
      direction="left"
      namespace="boostDrawer"
      type="drawer"
    >
      <template #body="{ isActive }">
        <boost-drawer
          v-if="getComponentVisible('boost-drawer')"
          v-cloak
          :is-active="isActive"
          :sort-by-options="sortByOptions"
          :total-products="total.products"
        />
      </template>
    </overlay>
  </div>
</template>

<script>

/**
 * Vue: Boost grid (boost-grid)
 * -----------------------------------------------------------------------------
 * Shared Boost functionality for collection and search pages.
 *
 * @param {Object} inGridContentBanner - In-grid content banner metafields.
 * @param {Object} liquid - Liquid data.
 * @param {Number} productsPerPage - Products per page based on in-grid content
 * banner width.
 * @param {Object} sortByOptions - Sort by options.
 * @param {String} storageKey - Session storage key.
 */
import { defineAsyncComponent } from 'vue'
import { mapActions, mapGetters } from 'vuex'

import BoostApiCall from '~global/boost-api-call/boost-api-call'
import BoostFilter from '~async/boost-grid/boost-filter'
import BoostPagination from '~async/boost-grid/boost-pagination'
import Btn from '~global/btn/btn'
import InGridContentBanner from '~async/in-grid-content-banner/in-grid-content-banner'
import Overlay from '~global/overlay/overlay'
import ProductCard from '~async/product-card/product-card'

import { formatBoostCollection, formatBoostPage, formatBoostProduct } from '~/helpers/format-boost'

export default {
  name: 'BoostGrid',

  components: {
    BoostApiCall,
    BoostDrawer: defineAsyncComponent({
      loader: () => import(
        /* webpackChunkName: 'component.boost-drawer' */
        '~async/boost-drawer/boost-drawer'
      ),
    }),
    BoostFilter,
    BoostPagination,
    Btn,
    InGridContentBanner,
    Overlay,
    ProductCard,
  },

  props: {
    inGridContentBanner: {
      type: Object,
      default: () => ({}),
    },
    liquid: {
      type: Object,
      default: () => ({
        description: '',
        handle: '',
        image: '',
        title: '',
        url: '',
      }),
    },
    productsPerPage: {
      type: Number,
      default: 16,
    },
    seoTitle: {
      type: String,
      default: '',
    },
    sortByOptions: {
      type: Object,
      default: () => ({}),
    },
    storageKey: {
      type: String,
      default: '',
    },
  },

  emits: {
    'boost-updated': (payload) => typeof payload === 'object',
  },

  data() {
    return {
      boostInitial: true,
      boostReady: false,
      choices: null,
      collections: [],
      filter: [],
      forceReplace: false,
      inGridContentBannerPosition: 5,
      linkWithoutPage: location.href.replaceAll(/[&|?]page=\d+/g, ''),
      no: {
        collections: true,
        pages: true,
        products: false,
      },
      page: 1,
      pages: [],
      products: [],
      query: null,
      response: {},
      state: 'loading',
      tab: 'products',
      total: {
        collections: 0,
        pages: 0,
        products: 0,
      },
    }
  },

  computed: {

    /**
     * Compute drawer choices.
     * @returns {String}
     */
    drawerChoices() {
      return this.getChoices()
    },

    /**
     * Compute endpoint for API.
     * @Returns {String}
     */
    endpoint() {
      let endpoint

      switch (cnvs.page.type) {
        case 'collection':
          endpoint = 'filter'
          break
        case 'search':
          switch (this.tab) {
            case 'collections':
              endpoint = 'search/collections'
              break
            case 'pages':
              endpoint = 'search/pages'
              break
            default:
              endpoint = 'search'
              break
          }
          break
      }

      return endpoint
    },

    /**
     * Compute if in-grid-content-banner available.
     * @returns {Boolean}
     */
    inGridContentBannerAvailable() {
      const hasImage = this.inGridContentBanner.bannerImage?.src
      const hasVideo = this.inGridContentBanner.bannerVideo?.sources

      return (
        this.tab === 'products' &&
        (hasImage || hasVideo) &&
        (!this.choices || !this.choices.includes('pf_'))
      )
    },

    /**
     * Compute in-grid-content-banner width.
     * @returns {String}
     */
    inGridContentBannerWidth() {
      let bannerClass = 'col xs3 l3'
      const bannerWidth = this.inGridContentBanner.bannerWidth || 1
      switch (bannerWidth) {
        case 2:
          bannerClass = 'col xs6 l6'
          break
        case 4:
          bannerClass = 'col xs6 l12'
          break
        default:
          bannerClass = 'col xs3 l3'
      }

      return bannerClass
    },

    /**
     * Compute pagination limit based on base.
     * @returns {Number}
     */
    limit() {
      if (cnvs.page.type === 'collection' && this.tab === 'products') {
        return this.productsPerPage
      }

      return cnvs.settings.pagination[this.tab]
    },

    /**
     * Compute no of current item.
     * @returns {Boolean}
     */
    noCurrent() {
      return this.no[this.tab]
    },

    /**
     * Compute total number of current item.
     * @returns {Number}
     */
    totalCurrent() {
      return this.total[this.tab]
    },

    /**
     * Compute total results.
     * @returns {Number}
     */
    totalResults() {
      return this.total.collections + this.total.pages + this.total.products
    },
  },

  watch: {

    /**
     * Watch drawer choices to update URL.
     */
    drawerChoices(choices) {
      if (choices === this.choices) {
        return
      }

      this.choices = choices

      /**
       * Don't set loading state if being updated from storage.
       */
      if (sessionStorage.getItem(this.storageKey)) {
        return
      }

      this.updateWindowLocationSearch(this.choices)
      this.boostReady = false
    },

    /**
     * Watch tab update to set boost not ready.
     */
    tab() {
      this.boostReady = false
      this.state = 'loading'
    },

    /**
     * Update meta title after change page.
     */
    page() {
      if (!this.seoTitle) {
        return
      }

      /**
       * Some characters like '&' were escaped, resulting in '&amp;' outputted
       * to the browser tab. Parse to 'unescape' them.
       */
      let title = new DOMParser().parseFromString(this.seoTitle, 'text/html').documentElement.textContent

      if (this.page > 1) {
        const pageString = this.$string('general.meta.page', {
          replace: { page: this.page },
        })
        title += ` - ${pageString}`
      }

      document.title = title
    },
  },

  mounted() {
    this.setEventListeners()
    this.getSearchParams()

    if (window.cnvs.page.type === 'search') {
      this.setSearchQuery()
    }
  },

  methods: {

    /**
     * Map Vuex actions.
     */
    ...mapActions({
      updateChoices: 'main-collection/updateChoices',
      updateFilters: 'main-collection/updateFilters',
    }),

    /**
     * Map Vuex getters.
     */
    ...mapGetters({
      getChoices: 'main-collection/getChoices',
      getFilters: 'main-collection/getFilters',
    }),

    /**
     * Set event listeners.
     */
    setEventListeners() {
      window.addEventListener('click', this.handleBodyClick)
      window.addEventListener('popstate', this.getSearchParams)
    },

    /**
     * Check window.location.search on load to see if filter choices exist
     * before making API call.
     * - Restore saved data and scroll if it exists and matches current URL.
     * - Otherwise remove it as it's no longer needed.
     */
    getSearchParams() {
      if (sessionStorage.getItem(this.storageKey)) {
        const storedData = JSON.parse(sessionStorage.getItem(this.storageKey))

        if (storedData.url === location.href) {
          this.updateDataFromStorage(storedData)
          return
        }

        sessionStorage.removeItem(this.storageKey)
      }

      if (!window.location.search.includes('?')) {
        this.choices = ''
        this.boostReady = false
        this.updateChoices(this.choices)
        return
      }

      const urlString = window.location.search.split('?')[1]

      const choicesArray = urlString.split('&').filter((choice) => {
        const filterOptionId = choice.split('=')[0]

        if (
          filterOptionId.includes('pf_') ||
          filterOptionId.includes('page') ||
          (filterOptionId.includes('sort') && choice !== '')
        ) {
          return choice
        }

        return false
      })

      const formatted = choicesArray.map((choice) => `&${choice}`)
      this.choices = formatted.join('')
      this.boostReady = false
      this.updateChoices(this.choices)
    },

    /**
     * If previous data retrieve it and use to restore products and position.
     * - Delete once finished.
     * - Update functions have to come first as updating this.choices triggers
     *   API call component watch which removes sessionStorage.
     * @param {Object} data - Stored data.
     */
    updateDataFromStorage(data) {
      this.updateFilters(data.vue.filter)
      this.updateChoices(data.vue.choices)

      Object.keys(data.vue).forEach((key) => {
        this[key] = data.vue[key]
      })

      scrollTo(0, data.scrollY)
      this.boostReady = true
      this.state = 'ready'
    },

    /**
     * Update window location search and browser history after choices updated.
     * @param {String} choices - Updated user choices.
     */
    updateWindowLocationSearch(choices) {
      let urlParameters = ''

      if (cnvs.page.type === 'search') {
        urlParameters += `?q=${this.query}`
      } else if (cnvs.page.collection === 'vendors' && !cnvs.page.handle) {
        urlParameters += `?q=${cnvs.page.title}`
      } else if (choices) {
        urlParameters += '?'
      }

      if (choices) {
        urlParameters += `&${choices}`
      }

      const url = location.search
        ? location.href.replace(location.search, urlParameters)
        : `${location.href}${urlParameters}`

      this.linkWithoutPage = url.replaceAll(/[&|?]page=\d+/g, '')

      window.history.pushState({}, '', url)
    },

    /**
     * Update pagination.
     * @param {String} pageNumber - Page number from choices.
     */
    updatePagination(pageNumber) {
      if (pageNumber) {
        if (this.totalCurrent > this[this.tab].length) {
          this.page = 1
        }

        return
      }

      this.page = parseInt(pageNumber)
    },

    /**
     * Update facets on load.
     */
    updateChoicesOnLoad() {
      const facetValues = [...document.querySelectorAll('[js-boost-facet="value"]')]

      const choicesArray = this.choices.split('&').filter((choice) => {
        return choice !== ''
      })

      let pageNumber

      facetValues.forEach((element) => element.classList.remove(this.$class('active')))

      choicesArray.forEach((choice) => {
        const value = choice.split('=')[1]
        const decoded = decodeURI(value)

        const activeValue = facetValues.find((element) => {
          return element.id === decoded
        })

        if (choice.includes('page')) {
          pageNumber = value
        }

        if (!activeValue) {
          return
        }

        activeValue.classList.add(this.$class('active'))
      })

      this.updatePagination(pageNumber)
    },

    /**
     * Clear choices when 'clear all' button is clicked.
     */
    clearChoices() {
      this.choices = ''
      this.boostReady = false
      this.updateChoices(this.choices)
      this.updateWindowLocationSearch(this.choices)
    },

    /**
     * Callback after user choices emitted from facets to filter.
     * @param {Object} response - Response object with choices update.
     */
    choicesUpdated(response) {
      const { action, forceReplace = false, loadMore = false, requestParam } = response

      const requestValue = decodeURI(requestParam.split('=')[1])
      let requestKey = decodeURI(requestParam.split('=')[0])
      if (requestKey.includes('&')) {
        requestKey = requestKey.slice(1)
      }

      const params = new URLSearchParams(this.choices)
      params.delete('page')

      if (action === 'add') {
        params.set(requestKey, requestValue)

        if (requestKey === 'sort' && requestValue === 'recommended') {
          params.delete('sort')
        }
      } else {
        params.delete(requestKey)
      }

      this.choices = params.toString().replaceAll('+', ' ')

      if (forceReplace) {
        this.forceReplace = true
      }

      if (!loadMore) {
        this.boostReady = false
      }

      if (requestKey === 'page') {
        this.page = Number(requestValue)
      }

      this.state = 'loading'
      this.updateChoices(this.choices)
      this.updateWindowLocationSearch(this.choices)
    },

    /**
     * Callback after a successful API call.
     * @param {Object} data - Response object from the API call.
     */
    boostUpdated(data) {
      this.response = data

      let newData = []
      const types = ['collections', 'pages', 'products']

      /**
       * Update type data.
       */
      newData = data[this.tab].map((object) => {
        let formattedData = []

        switch (this.tab) {
          case 'collections':
            formattedData = formatBoostCollection(object)
            break
          case 'pages':
            formattedData = formatBoostPage(object)
            break
          case 'products':
            formattedData = formatBoostProduct(object)
            break
        }

        return formattedData
      })

      if (this.forceReplace) {
        this[this.tab] = newData
        this.forceReplace = false
      } else {

        /**
         * Update data based on current tab.
         */
        switch (data.action) {
          case 'replace':
            this[this.tab] = newData
            break
          case 'append':
            this[this.tab] = [...this[this.tab], ...newData]
            break
          case 'prepend':
            this[this.tab] = [...newData, ...this[this.tab]]
            break
        }
      }

      types.forEach((type) => {
        const total = `total_${type.slice(0, -1)}`

        if (typeof data[total] === 'number') {
          this.total[type] = data[total]
        }

        this.no[this.tab] = !this[this.tab].length
      })

      /**
       * Filters.
       */
      if (this.tab === 'products') {
        this.filter = data.filter?.options
        this.setFilterSwatches()
        this.updateFilters(this.filter)
      }

      /**
       * Misc data.
       */
      this.page = data.page
      this.state = 'ready'
      this.boostReady = true
      this.boostInitial = false

      /**
       * Emit for main search header.
       */
      this.$nextTick().then(() => {
        this.$emit('boost-updated', {
          query: this.query,
          totalResults: this.totalResults,
        })
      })
    },

    /**
     * Callback from Shopify Markets price data is ready.
     * - Update existing products data with new prices by matching ID.
     * - If no localised product or prices then reveal price.
     * - Better to show an incorrect price than a loading state.
     * @param {Array} prices - Localised price data.
     */
    boostPricesUpdated(prices) {
      if (!prices.length) {
        this.products = this.products.map((product) => {
          return {
            ...product,
            price: {
              ...product.price,
              loading: false,
            },
          }
        })

        return
      }

      this.products = this.products.map((product) => {
        const localisedProduct = prices.find((localProduct) => {
          return localProduct.id === Number(product.id)
        })

        if (!localisedProduct) {
          return {
            ...product,
            price: {
              ...product.price,
              loading: false,
            },
          }
        }

        return {
          ...product,
          price: localisedProduct.price,
          compareAtPrice: localisedProduct.compareAtPrice,
        }
      })
    },

    /**
     * Event handler to close any open filters on body click.
     * @param {Event} event - Click event.
     */
    handleBodyClick(event) {
      const facets = document.querySelectorAll('[js-boost-facet="facet"]')
      const sortBy = document.querySelector('[js-boost-filter="sortBy"]')
      let bodyClick = true

      facets.forEach((facet) => {
        if (!facet.contains(event.target)) {
          return
        }

        bodyClick = false
      })

      if (sortBy !== null && sortBy.contains(event.target)) {
        bodyClick = false
      }

      if (!bodyClick) {
        return
      }

      facets.forEach((facet) => {
        facet.classList.remove(this.$class('active'))
      })

      if (sortBy === null) {
        return
      }

      sortBy.classList.remove(this.$class('active'))
    },

    /**
     * Set swatches for use in Boost Filter.
     */
    setFilterSwatches() {
      const data = this.filter.find((option) => {
        return option.filterType === 'opt_colour'
      })

      if (!data) {
        return
      }

      const swatches = data.values.map((swatch) => {
        const key = swatch.key.toLowerCase()

        const formatted = {
          key,
          swatch: `color-${key}_12x12.png`,
        }

        return formatted
      })

      window.cnvs = window.cnvs || {}
      window.cnvs.filterSwatches = swatches
    },

    /**
     * Set search query from URL parameters.
     */
    setSearchQuery() {
      if (!window.location.search.includes('?')) {
        this.query = false
        return
      }

      const urlParams = window.location.search.split('?')[1].split('&')
      const queryParam = urlParams.find((param) => {
        return param.includes('q=')
      })

      if (!queryParam) {
        return
      }

      this.query = queryParam.split('=')[1]
    },

    /**
     * Handle view product click.
     * - Save collection data to session storage so it can be retrieved when
     *   using browser navigation to go back and restore scroll position.
     * @param {String} url - URL to navigate to when complete.
     */
    handleViewProduct(url) {
      const data = {
        scrollY,
        url: location.href,
        vue: {
          choices: this.choices,
          filter: this.filter,
          page: this.page,
          products: this.products,
          total: this.total,
        },
      }

      sessionStorage.setItem(this.storageKey, JSON.stringify(data))
      location.href = url
    },

    /**
     * Handle tab update.
     * @param {String} type - Type of tab to update to.
     */
    handleUpdateTab(type) {
      this.tab = type
    },
  },
}
</script>

<style lang="scss">
@import './boost-grid';
@import '~async/product-card/product-card';
</style>
